import {
  Box,
  Skeleton,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import SlideArrows from '@jobseeker/lotus/components/SlideArrows';
import {Image} from '@jobseeker/lotus/components/Image';
import {useRef, useState, useEffect} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import type {AdsElementType} from '@jobseeker/common-core/types';
import {
  TrackEventUBA,
  parseShowcaseUrl,
  getUbaPageName,
} from '@jobseeker/common-core/utils';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';

const WomensDaySpecial = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [ads, setAds] = useState<AdsElementType[]>([]);
  const [adsLoaded, setAdsLoaded] = useState(false);

  const interceptor: /* eslint-disable @typescript-eslint/no-explicit-any */ any =
    useInterceptor();
  const {
    ads: {wowPlatinumPartners},
  } = interceptor;

  const slot = wowPlatinumPartners?.slot ?? '';
  const shouldLoad = Boolean(slot);

  useLoadAds(slot, setAds, {}, false, shouldLoad, setAdsLoaded, shouldLoad);
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollDistance = isMobile ? 250 : 370;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollDistance : scrollDistance,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (!hasTrackedView && ads.length) {
      const companyIDSArray = ads
        .map(ad => ad?.companyId || parseShowcaseUrl(ad?.link))
        .filter(Boolean) as Array<string | number>;
      const companyNamesArray = ads
        .map(ad => ad?.title)
        .filter(Boolean) as string[];

      TrackEventUBA('companyBannerView', 'view', {
        ref:
          typeof window !== 'undefined'
            ? new URL(window.location.href).searchParams.get('ref') || ''
            : '',
        source: 'WomensDaySpecial',
        companyIDSArray,
        companyNamesArray,
        page: getUbaPageName(),
        sessionUsername:
          typeof window !== 'undefined'
            ? sessionStorage.getItem('userId') || ''
            : '',
      });
      setHasTrackedView(true);
    }
  }, [ads, hasTrackedView]);

  if (!ads?.length || !adsLoaded) return null;

  return (
    <Box
      sx={{
        display: isAppIimjobs ? 'block' : 'flex',
        justifyContent: isAppIimjobs ? undefined : 'center',
        width: isAppIimjobs ? undefined : '100%',
        ...(isAppIimjobs && {
          marginTop: {xxs: 0, md: 4},
          marginBottom: {xxs: '-46px', md: 0},
        }),
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(180deg, #FBDBE3 0%, #FFCDF5 100%)',
          py: {xs: 3, sm: 3.5, md: 4},
          pl: {xs: 2, sm: 3, md: 3},
          pr: {xs: 0, sm: 3, md: 3},
          position: 'relative',
          overflow: 'hidden',
          mt: isAppIimjobs ? {xs: 3, md: 0} : {xs: '-75px', md: '-35px'},
          mx: isAppIimjobs
            ? {xs: '16px', sm: 'auto'}
            : {xxs: '16px', md: '24px'},
          maxWidth: {
            xs: '379px',
            sm: '95%',
            md: isAppIimjobs ? '100%' : '100%',
          },
          borderRadius: {xs: '20px', sm: '16px'},
          mb: isAppIimjobs ? {xs: -3, md: 4} : {xxs: '35px', sm: 4},
          // mt: {xs: isAppIimjobs ? 3 : '-75px', sm: 0},
        }}
      >
        {/* Women's Day Special Badge */}
        <Box display="flex" justifyContent="center" mb={1}>
          <Box
            sx={{
              backgroundColor: '#fff',
              px: 3,
              py: '4px',
              transform: 'skewX(-10deg)',
            }}
          >
            <Typography
              sx={{
                color: '#FF27F2',
                fontSize: 14,
                fontWeight: isAppIimjobs ? 600 : {xs: 600, md: 700},
                textTransform: 'uppercase',
                transform: 'skewX(10deg)',
                textAlign: 'center',
              }}
            >
              Women&apos;s Day Special
            </Typography>
          </Box>
        </Box>

        {/* Heading + Navigation Arrows */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{mb: {xs: 2.5, md: 3}, position: 'relative'}}
        >
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: {xs: 16, sm: 20, md: 26},
              fontWeight: isAppIimjobs ? {xs: 600, md: 400} : 600,
              color: '#333333',
            }}
          >
            Top Employers Championing{' '}
            <Box component="br" sx={{display: {xs: 'block', md: 'none'}}} />
            Women Talent
          </Typography>

          {/* Arrows - top right */}
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              display: {xs: 'none', sm: 'flex'},
            }}
          >
            <SlideArrows
              scrollLeft={() => scroll('left')}
              scrollRight={() => scroll('right')}
              disableLeft={false}
              disableRight={false}
            />
          </Box>
        </Box>

        {/* Carousel */}
        <Box sx={{position: 'relative'}}>
          <AnimatePresence mode="wait">
            {!adsLoaded ? (
              <motion.div
                key="womens-day-skeleton"
                initial={{opacity: 1}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                transition={{duration: 0.2}}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    overflowX: 'auto',
                    scrollBehavior: 'smooth',
                    '&::-webkit-scrollbar': {display: 'none'},
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    '&::after': {
                      content: '""',
                      flex: '0 0 5px',
                      display: {xs: 'block', sm: 'none'},
                    },
                  }}
                >
                  {Array.from({length: isMobile ? 2 : 4}).map((_, i) => (
                    <Box key={`womens-skeleton-${i}`} sx={{flex: '0 0 auto'}}>
                      <Skeleton
                        variant="rectangular"
                        width={isMobile ? 270 : 320}
                        height={isMobile ? 90 : 117}
                        sx={{borderRadius: '15px'}}
                      />
                    </Box>
                  ))}
                </Box>
              </motion.div>
            ) : ads?.length > 0 ? (
              <motion.div
                key="womens-day-content"
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                exit={{opacity: 0, y: 10}}
                transition={{duration: 0.15}}
              >
                <Box
                  ref={scrollRef}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    overflowX: 'auto',
                    scrollBehavior: 'smooth',
                    '&::-webkit-scrollbar': {display: 'none'},
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    '&::after': {
                      content: '""',
                      flex: '0 0 5px',
                      display: {xs: 'block', sm: 'none'},
                    },
                  }}
                >
                  {ads.map((adObj: AdsElementType, i: number) => (
                    <Box
                      component="a"
                      href={adObj.link || '#'}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      key={`womens-day-ad-${i}`}
                      sx={{
                        flex: '0 0 auto',
                        cursor: 'pointer',
                        display: 'block',
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.03)',
                        },
                      }}
                      onClick={() => {
                        TrackEventUBA('companyBannerClick', 'click', {
                          ref:
                            typeof window !== 'undefined'
                              ? new URL(window.location.href).searchParams.get(
                                  'ref'
                                ) || ''
                              : '',
                          source: 'WomensDaySpecial',
                          companyID: adObj.companyId || '',
                          companyName: adObj.title || '',
                          page: getUbaPageName(),
                          sessionUsername:
                            sessionStorage.getItem('userId') || '',
                        });
                      }}
                    >
                      <Image
                        src={adObj.imageUrl || ''}
                        alt={adObj.title || "Women's Day partner"}
                        width={isMobile ? 270 : 320}
                        height={isMobile ? 90 : 117}
                        style={{
                          objectFit: 'cover',
                          width: isMobile ? '270px' : '320px',
                          height: isMobile ? '90px' : '117px',
                          borderRadius: '15px',
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </Box>

        {/* View All Jobs CTA */}
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            component="a"
            href="/women_at_work?ref=homepage"
            variant="outlined"
            sx={{
              border: isAppIimjobs ? '1px solid #333333' : '1px solid #E9630C',
              color: isAppIimjobs ? '#333333' : '#E9630C',
              borderRadius: isAppIimjobs ? '25px' : '8px',
              width: isAppIimjobs ? 'auto' : 180,
              height: isAppIimjobs ? 36 : 42,
              px: isAppIimjobs ? 2.5 : 0,
              py: 0,
              fontSize: 14,
              fontWeight: isAppIimjobs ? 500 : 700,
              lineHeight: '30px',
              textTransform: 'none',
              backgroundColor: isAppIimjobs ? 'transparent' : '#FFFAF1',
              '&:hover': {
                backgroundColor: isAppIimjobs ? '#333333' : '#e9630c',
                color: isAppIimjobs ? '#fff' : '#fff',
                borderColor: isAppIimjobs ? 'rgba(0,0,0,0.15)' : '#E9630C',
              },
            }}
          >
            View all jobs
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default WomensDaySpecial;
